import { useSearchParams } from 'next/navigation'
import { type Key } from 'swr'
import useSWRMutation, { type SWRMutationResponse } from 'swr/mutation'

import {
  type CartControllerAddResponse,
  type CartControllerV4ApiCartServiceCartPostAddToCartEntryLocalizedRequest,
  CartControllerV4ApiFactory,
  type PimProductV12,
  type PimProductV13,
  type PimProductWithVariantsV1,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'
import { type RxByPrincipleSelectableReason } from '@redteclab/api/clients/composition-service-transactions'
import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { useGlooApi } from '../../../api'
import { useGlobalConfigContext } from '../../../global-config'
import { type CartIntermediateProductInfoSellerData } from '../../components/CartIntermediateProductInfo/CartIntermediateProductInfoSellerLink/CartIntermediateProductInfoSellerLink'
import { buildRxByPrincipleParams } from '../buildRxByPrincipleParams'

const getCartIntermediateApiSwrProductAddToCartKey = (): Key => {
  return 'cartIntermediateApiSwrProductAddToCart'
}

export type CartIntermediateAddToCartProps = {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  offerId?: string
  product?:
    | GkAirRecommendedProductDataWeb
    | PimProductV12
    | PimProductV13
    | PimProductWithVariantsV1
  quantity: number
  redpoints: number
  rxByPrincipleSelectedReason?: RxByPrincipleSelectableReason
  sellerData?: CartIntermediateProductInfoSellerData
}

export const useCartIntermediateApiSwrProductAddToCartMutation = ({
  currentPrescriptionType,
  offerId,
  quantity,
  rxByPrincipleSelectedReason,
}: CartIntermediateAddToCartProps): SWRMutationResponse<CartControllerAddResponse | null> => {
  const config = useGlobalConfigContext()
  const client = useGlooApi(CartControllerV4ApiFactory)

  const searchParams = useSearchParams()
  const abcbp = searchParams.get('abcbp') ?? ''
  const hasCbpQueryParam = typeof abcbp === 'string' && abcbp === '1'

  const fetcher = async (): Promise<CartControllerAddResponse | null> => {
    if (!offerId) {
      return null
    }

    const payload: CartControllerV4ApiCartServiceCartPostAddToCartEntryLocalizedRequest =
      {
        Accept_Language: config.locale,
        CartV2CartEntryRequestV1: {
          hasCbpDiscount: hasCbpQueryParam,
          offerId,
          prescriptionType: currentPrescriptionType,
          quantity,
          rxByPrinciple: buildRxByPrincipleParams(rxByPrincipleSelectedReason),
        },
        language: config.language,
        tenant: config.tenantAndEnv,
      }

    const response =
      await client.cartServiceCartPostAddToCartEntryLocalized(payload)

    return response.data
  }

  return useSWRMutation(getCartIntermediateApiSwrProductAddToCartKey(), fetcher)
}
